function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
import { Kind } from "graphql";
import { ROOT_QUERY_INCLUDE_VARIABLE_NAME } from "./constants";
import { getFragmentArgDefinitions, getMetadatafromArgDefinition } from "../documentUtils/argumentDefinitions";
import { isDefinedOnQuery } from "../documentUtils/composableFragment";
import { getFragmentDefinitions } from "../documentUtils/fragment";
import { createQueryDocument } from "../documentUtils/query";
import { createVariableDefinitionNode } from "../documentUtils/variable";
import addTypename from "../../queryUtils/addTypename";
import { generateHashForDefinitionNode } from "../../queryUtils/documentHashing";
const buildIncludeDirective = (currentConcatenatedHash)=>({
        kind: Kind.DIRECTIVE,
        name: {
            kind: Kind.NAME,
            value: 'include'
        },
        arguments: [
            {
                kind: Kind.ARGUMENT,
                name: {
                    kind: Kind.NAME,
                    value: 'if'
                },
                value: {
                    kind: Kind.VARIABLE,
                    name: {
                        kind: Kind.NAME,
                        value: `${currentConcatenatedHash}___${ROOT_QUERY_INCLUDE_VARIABLE_NAME}`
                    }
                }
            }
        ]
    });
function getFragmentsInfoByName(ast) {
    const fragmentDefs = getFragmentDefinitions(ast);
    const fragmentMap = {};
    fragmentDefs.forEach((node)=>{
        const fragmentHash = generateHashForDefinitionNode(ast, node);
        fragmentMap[node.name.value] = {
            node,
            hash: fragmentHash,
            isOnQuery: isDefinedOnQuery(node)
        };
    });
    return fragmentMap;
}
function getResultQueryName(rootFragmentName) {
    return rootFragmentName.replace('CompositionFragment', 'CompositionQuery');
}
function getFirstFragmentName(ast) {
    const fragmentDefs = getFragmentDefinitions(ast);
    return fragmentDefs[0].name.value;
}
export default function transformCompositionFragmentToQuery(ast) {
    const documentWithTypename = addTypename(ast);
    const rootFragmentName = getFirstFragmentName(documentWithTypename);
    const resultQueryName = getResultQueryName(rootFragmentName);
    const fragmentInfoByNameMap = getFragmentsInfoByName(documentWithTypename);
    const { topLevelFields, topLevelVariableDefinitions, prefixToFragmentName } = transformNestedFragmentsToTopLevelFields(fragmentInfoByNameMap, rootFragmentName);
    const fragmentsToAppend = [];
    // We need to append all fragments that were not defined on Query. These are
    // the framgents that other depend on and so they need to be included in the
    // transformed document.
    Object.values(fragmentInfoByNameMap).forEach((fragmentInfo)=>{
        if (!fragmentInfo.isOnQuery) {
            fragmentsToAppend.push(fragmentInfo.node);
        }
    });
    const transformedQueryDocument = createQueryDocument(resultQueryName, topLevelVariableDefinitions, topLevelFields, fragmentsToAppend);
    return {
        prefixToFragmentName,
        query: transformedQueryDocument
    };
}
function transformNestedFragmentsToTopLevelFields(fragmentInfoByNameMap, rootFragmentName) {
    const topLevelFields = [];
    const topLevelVariableDefinitions = [];
    const prefixToFragmentName = {};
    // Kick off a breadth-first search algorithm. We are stepping into each
    // fragment, starting by `CompositionFragment`. Once we find a leaf, which in
    // this case is a normal field and not a fragment spread, we create a new
    // entry in topLevelFields
    const fragmentsToProcess = [];
    // Initialize BFS with `CompositionFragment`
    fragmentsToProcess.push({
        fragmentName: rootFragmentName,
        parentAccumulatedHash: '',
        variableScopeHash: ''
    });
    while(fragmentsToProcess.length !== 0){
        const firstElement = fragmentsToProcess.shift();
        const { fragmentName, parentAccumulatedHash, variableScopeHash } = firstElement;
        const fragmentInfo = fragmentInfoByNameMap[fragmentName];
        if (!fragmentInfo) {
            throw new Error(`Fragment ${fragmentName} not found in fragment info map`);
        }
        const fragmentNode = fragmentInfo.node;
        const argDefDirective = getFragmentArgDefinitions(fragmentNode);
        const fragmentHasArguments = argDefDirective && argDefDirective.arguments && argDefDirective.arguments.length > 0;
        const currentConcatenatedHash = parentAccumulatedHash.length === 0 ? fragmentInfo.hash : `${parentAccumulatedHash}___${fragmentInfo.hash}`;
        // Use current hash for variable scope if this fragment has arguments,
        // otherwise inherit from parent
        const currentVariableScopeHash = fragmentHasArguments ? currentConcatenatedHash : variableScopeHash;
        // Process the selections of this fragment.
        fragmentNode.selectionSet.selections.forEach((selection, selectionIndex)=>{
            //  It's either a top level field (a "leaf")
            if (selection.kind === Kind.FIELD && selection.name.value !== '__typename') {
                const newTopLevelField = createTopLevelFieldFromFragment(selection, currentVariableScopeHash);
                topLevelFields.push(newTopLevelField);
            } else if (// Or it's a spread of a nested fragment
            selection.kind === Kind.FRAGMENT_SPREAD) {
                // Add this child fragment to the queue
                const fragmentSpread = selection;
                const childFragmentName = fragmentSpread.name.value;
                const hashWithSelectionIndex = `${currentConcatenatedHash}___S${selectionIndex}`;
                fragmentsToProcess.push({
                    fragmentName: childFragmentName,
                    parentAccumulatedHash: hashWithSelectionIndex,
                    variableScopeHash: currentVariableScopeHash
                });
            }
        });
        const isRootFragment = fragmentName === rootFragmentName;
        if (isRootFragment) {
            continue;
        }
        prefixToFragmentName[currentVariableScopeHash] = fragmentNode.name.value;
        // Process the @arguments directive of this fragment.
        // We need to get all arguments of this fragment and add them to topLevelVariableDefinitions.
        // Get the list of variables that need to be defined on query
        if (fragmentHasArguments) {
            argDefDirective.arguments.forEach((arg)=>{
                const { argumentName, typeValue } = getMetadatafromArgDefinition(arg, rootFragmentName);
                const newTopLevelVariable = createVariableDefinitionNode(Kind.NAMED_TYPE, `${currentConcatenatedHash}___${argumentName}`, typeValue);
                topLevelVariableDefinitions.push(newTopLevelVariable);
            });
            const includeVariable = createVariableDefinitionNode(Kind.NON_NULL_TYPE, `${currentConcatenatedHash}___${ROOT_QUERY_INCLUDE_VARIABLE_NAME}`, 'Boolean');
            topLevelVariableDefinitions.push(includeVariable);
        }
    }
    return {
        prefixToFragmentName,
        topLevelFields,
        topLevelVariableDefinitions
    };
}
function getArgVariableName(arg) {
    const argValueNode = arg.value;
    if (argValueNode.kind !== 'Variable') {
        throw new Error('I cannot handle non-variable as argument value yet');
    }
    return argValueNode.name.value;
}
function getPrefixedArguments(selection, hashValue) {
    const { arguments: args } = selection;
    if (!args) {
        throw new Error('top level field does not have arguments');
    }
    return args === null || args === void 0 ? void 0 : args.map((arg)=>_object_spread_props(_object_spread({}, arg), {
            value: {
                kind: Kind.VARIABLE,
                name: {
                    kind: Kind.NAME,
                    value: `${hashValue}___${getArgVariableName(arg)}`
                }
            }
        }));
}
function createTopLevelFieldFromFragment(selection, currentConcatenatedHash) {
    var _selection_alias;
    const prefixedArguments = getPrefixedArguments(selection, currentConcatenatedHash);
    const topLevelFieldName = selection.name.value;
    const existingAlias = (_selection_alias = selection.alias) === null || _selection_alias === void 0 ? void 0 : _selection_alias.value;
    const topLevelFieldAlias = existingAlias ? `${currentConcatenatedHash}___${existingAlias}` : `${currentConcatenatedHash}___${topLevelFieldName}`;
    const updatedDirectives = addIncludeToDirectives(selection.directives, currentConcatenatedHash);
    const newTopLevelField = _object_spread_props(_object_spread({}, selection), {
        alias: {
            kind: Kind.NAME,
            value: topLevelFieldAlias
        },
        arguments: prefixedArguments,
        directives: updatedDirectives
    });
    return newTopLevelField;
}
function addIncludeToDirectives(directives, currentConcatenatedHash) {
    if (!directives) {
        return [];
    }
    // Add an include directive on every top level field
    const includeDirective = buildIncludeDirective(currentConcatenatedHash);
    return [
        ...directives,
        includeDirective
    ];
}

//# sourceMappingURL=transformCompositionFragmentToQuery.js.map